'use client';

import { useEffect } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { AppButton } from '@/components/common';
import { trackAndLogError } from '@/lib/errors/errorTracker';

/**
 * 전역 에러 바운더리
 * 애플리케이션 전체에서 발생하는 예외를 포착하고 표시합니다.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // 에러 추적 시스템에 기록
    trackAndLogError(error, {
      component: 'GlobalErrorBoundary',
      action: 'uncaught_error',
    }, 'critical');
  }, [error]);

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* 아이콘 */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* 제목 및 설명 */}
        <div className="space-y-2">
          <h1 className="heading-2 text-neutral-900">
            오류가 발생했습니다
          </h1>
          <p className="body-base text-neutral-600">
            예기치 않은 오류가 발생했습니다.
            잠시 후 다시 시도해 주세요.
          </p>
        </div>

        {/* 에러 상세 표시 (개발 환경에서만) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-red-800 break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-2">
                Digest: {error.digest}
              </p>
            )}
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <AppButton
            onClick={reset}
            variant="primary"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            다시 시도
          </AppButton>
          <AppButton
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            홈으로 이동
          </AppButton>
        </div>

        {/* 도움말 링크 */}
        <p className="body-small text-neutral-500">
          문제가 지속되면{' '}
          <a href="mailto:support@auto-playlist.com" className="text-purple-600 hover:underline">
            고객 지원
          </a>
          에 문의하세요.
        </p>
      </div>
    </div>
  );
}
